import { VideoType } from '../videoPreview/constants';
import {
  getGoogleDriveVideoId,
  getGoogleDriveVideoSrc,
  getGoogleDriveVideoThumbnail,
  getYouTubeVideoId,
  getYouTubeVideoSrc,
  getYouTubeVideoThumbnail,
} from '../../utils/extractThumbnail';
import {
  MustWatchVideoNotification,
  VideoNotificationAudienceType,
  VideoNotificationDto,
} from './constants';

const toBool = (value: unknown, fallback: boolean) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  return value === true || value === 1 || value === '1' || value === 'true';
};

/** csv string | array | null → number[] */
export const toIdArray = (
  value?: string | Array<number | string> | null
): number[] => {
  if (!value) {
    return [];
  }
  const parts = Array.isArray(value) ? value : String(value).split(',');
  return parts
    .map((part) => Number(String(part).trim()))
    .filter((n) => Number.isFinite(n));
};

/** ISO string | epoch seconds | epoch millis → epoch millis */
const toMillis = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value === 'number') {
    // seconds-based timestamps are ~1e9, millis ~1e12
    return value < 1e11 ? value * 1000 : value;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/** youtube / google-drive link → embeddable iframe src */
export const resolveEmbedSrc = (videoUrl?: string | null) => {
  if (!videoUrl) {
    return undefined;
  }
  const youtubeId = getYouTubeVideoId({ videoUrl });
  if (youtubeId) {
    return getYouTubeVideoSrc({ videoId: youtubeId });
  }
  const driveId = getGoogleDriveVideoId({ videoUrl });
  if (driveId) {
    return getGoogleDriveVideoSrc({ videoId: driveId });
  }
  return undefined;
};

/** youtube / google-drive link → poster image, when one can be derived */
export const resolveThumbnail = (videoUrl?: string | null) => {
  if (!videoUrl) {
    return undefined;
  }
  const youtubeId = getYouTubeVideoId({ videoUrl });
  if (youtubeId) {
    return getYouTubeVideoThumbnail({ videoId: youtubeId });
  }
  const driveId = getGoogleDriveVideoId({ videoUrl });
  if (driveId) {
    return getGoogleDriveVideoThumbnail({ videoId: driveId });
  }
  return undefined;
};

const toAudienceType = (value?: string) => {
  switch (value) {
    case VideoNotificationAudienceType.AGENCY:
      return VideoNotificationAudienceType.AGENCY;
    case VideoNotificationAudienceType.AGENTS:
      return VideoNotificationAudienceType.AGENTS;
    default:
      return VideoNotificationAudienceType.ALL_AGENTS;
  }
};

/**
 * Turns an API video notification into the shape the UI uses.
 * Returns null when there is nothing playable to show.
 */
export const normalizeVideoNotification = (
  dto?: VideoNotificationDto | null
): MustWatchVideoNotification | null => {
  if (!dto || typeof dto !== 'object' || dto.id === undefined) {
    return null;
  }

  const videoType =
    dto.videoType === VideoType.MP4 ? VideoType.MP4 : VideoType.URL;
  const source = dto.videoPath || undefined;
  const videoPath = videoType === VideoType.MP4 ? source : undefined;
  const embedSrc = videoType === VideoType.URL ? resolveEmbedSrc(source) : undefined;

  // An mp4 entry with no file is unplayable; a link we cannot embed still
  // belongs in the list — the modal shows its "video unavailable" fallback.
  if (videoType === VideoType.MP4 && !videoPath) {
    return null;
  }

  return {
    id: Number(dto.id),
    title: dto.title || '',
    description: dto.description || '',
    videoType,
    videoPath,
    embedSrc,
    thumbnail: videoType === VideoType.URL ? resolveThumbnail(source) : undefined,
    dateCreatedMs: toMillis(dto.dateCreated),
    targetType: toAudienceType(dto.targetType),
    agencyIds: toIdArray(dto.agencyIds),
    agentIds: toIdArray(dto.agentIds),
    isActive: toBool(dto.isActive, true),
    isWatched: toBool(dto.isWatched, false),
    isMustWatch: toBool(dto.mustWatch, true),
    raw: dto,
  };
};

/**
 * Sort order for the notification panel: unseen must-watch first, then unseen
 * optional, then everything already watched — each group newest first.
 */
const sortWeight = (n: MustWatchVideoNotification) => {
  if (n.isWatched) {
    return 2;
  }
  return n.isMustWatch ? 0 : 1;
};

export const sortVideoNotifications = (
  list: MustWatchVideoNotification[]
): MustWatchVideoNotification[] =>
  [...list].sort((a, b) => {
    const weightDiff = sortWeight(a) - sortWeight(b);
    if (weightDiff !== 0) {
      return weightDiff;
    }
    return (b.dateCreatedMs ?? 0) - (a.dateCreatedMs ?? 0);
  });

/**
 * Normalizes an API list, drops inactive/unplayable entries and applies the
 * panel sort order.
 */
export const normalizeVideoNotifications = (
  list?: VideoNotificationDto[] | null
): MustWatchVideoNotification[] => {
  if (!Array.isArray(list)) {
    return [];
  }
  const normalized = list
    .map(normalizeVideoNotification)
    .filter((n): n is MustWatchVideoNotification => !!n && n.isActive);

  return sortVideoNotifications(normalized);
};

/** Everything the user has not watched yet — drives the bell badge count. */
export const getUnseenNotifications = (list: MustWatchVideoNotification[]) =>
  list.filter((n) => !n.isWatched);

/**
 * Unseen must-watch videos — the only thing that locks the UI, and the queue
 * the modal arrows navigate while a required video is open. Optional videos
 * never block, even when they are the last unseen ones left.
 */
export const getUnseenMustWatch = (list: MustWatchVideoNotification[]) =>
  list.filter((n) => !n.isWatched && n.isMustWatch);

/**
 * Unseen non-blocking videos — the queue the modal arrows navigate while an
 * optional video is open.
 */
export const getUnseenOptional = (list: MustWatchVideoNotification[]) =>
  list.filter((n) => !n.isWatched && !n.isMustWatch);

/** A single notification still requires blocking attention. */
export const isMustWatchPending = (
  notification: MustWatchVideoNotification | null
) =>
  !!notification &&
  notification.isActive &&
  notification.isMustWatch &&
  !notification.isWatched;
